<script setup lang="ts">
import { useBlogStore } from '~/stores/BlogStore'
import type { SectionArticleListRecord } from '~/types/datocms'

interface SectionArticlesProps {
  data: SectionArticleListRecord
  moduleIndex: number
}

const props = defineProps<SectionArticlesProps>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)

const appConfig = useAppConfig()
const route = useRoute()
const store = useBlogStore()

// Set pages per data load
store.setItemsPerPage(props.data.itemsPerPage as number)

function loadData(initial: boolean = true) {
  // Handle initial load vs. loadMore
  if (initial) {
    store.setLoading(true)
    store.resetArticles()
  } else {
    store.setLoadingMore(true)
  }

  if (route.query.category) {
    // Fetch Articles by Category
    const cat = store.getCategoryBySlug(route.query.category as string)

    if (cat) {
      store.pushFilter(cat)
      // Filtered by Category (DatoCMS record ID)
      store.fetchArticles(cat.id)
    }
  } else {
    // Unfiltered / All Article
    store.pushFilter(null)
    store.fetchArticles()
  }
}

watch(
  () => route.query,
  () => {
    loadData()
  }
)

onMounted(() => {
  if (!store.allArticles.length || route.query.category !== store.filteredBy?.slug) {
    loadData()
  }
})
</script>

<template>
  <div class="container container-max-width page-pt" :class="invertedStyles">
    <ClientOnly v-if="appConfig.ui.enableFluidThree">
      <FluidBlack />
    </ClientOnly>
    <Heading
      v-if="data.title"
      class="mb-5 lg:mb-13"
      :size="1"
      :level="level"
      :is-semantic="isSemantic"
      ><TextReveal
        ><TextClip class="w-fit bg-[160%_53%]">{{ data.title }}</TextClip></TextReveal
      ></Heading
    >
    <ArticlePageFilter v-if="data.categories.length" :categories="data.categories" />
    <ArticleList
      layout="mixed"
      class="relative min-h-screen"
      :data="store.allArticles"
      :is-loading="store.isLoading"
      :load-more-label="data.linkMore"
    />
    <div class="min-h-[124px] py-8 pb-12 text-center">
      <Transition
        enter-from-class="opacity-0 scale-75"
        leave-to-class="opacity-0 scale-75"
        enter-active-class="transition-all duration-500"
        leave-active-class="transition-all duration-300"
      >
        <Button
          v-show="data.linkMore && store.isButtonVisible"
          :label="data.linkMore"
          :loading="store.isLoadingMore"
          class="min-w-[168px]"
          variant="outline"
          @click="loadData(false)"
        />
      </Transition>
    </div>
  </div>
</template>
